import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";

interface Props {
  clubId: string;
  isAdmin: boolean;
}

function formatDate(d: Date | string) {
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function PollCard({ clubId, isAdmin }: Props) {
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  const { data: polls, isLoading } = trpc.polls.list.useQuery({ clubId });

  const openPoll = trpc.polls.open.useMutation({
    onSuccess: () => utils.polls.list.invalidate({ clubId }),
  });
  const closePoll = trpc.polls.close.useMutation({
    onSuccess: () => utils.polls.list.invalidate({ clubId }),
  });

  const [deadlineDays, setDeadlineDays] = useState(7);

  if (isLoading) return null;

  const activePoll = polls?.find((p) => p.state === "OPEN");
  const recentClosed = polls?.filter((p) => p.state !== "OPEN").slice(0, 3) ?? [];

  return (
    <div className="space-y-4">
      {activePoll ? (
        <div
          className="rounded-xl p-4 space-y-3"
          style={{ background: "var(--color-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-sm)" }}
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-accent)" }}>
                {activePoll.parentPollId ? t("polls.card.runoffOpen") : t("polls.card.pollOpen")}
              </p>
              <p className="text-sm mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
                {activePoll.voteCount}/{activePoll.eligibleCount} {t("polls.card.voted")} · {t("polls.card.closes", { date: formatDate(activePoll.deadline) })}
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={closePoll.isPending}
                  onClick={() => closePoll.mutate({ pollId: activePoll.id })}
                  style={{ color: "var(--color-muted-foreground)" }}
                >
                  {t("polls.card.closeButton")}
                </Button>
              )}
              <Link
                to={`/clubs/${clubId}/polls/${activePoll.id}`}
                className="text-xs px-3 py-1.5 rounded-md font-medium transition-all flex-shrink-0"
                style={{ color: "var(--color-accent)", background: "var(--color-accent-subtle)", textDecoration: "none" }}
              >
                {t("polls.card.voteButton")}
              </Link>
            </div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-secondary)" }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${activePoll.eligibleCount > 0 ? (activePoll.voteCount / activePoll.eligibleCount) * 100 : 0}%`,
                background: "var(--color-accent)",
              }}
            />
          </div>
        </div>
      ) : isAdmin ? (
        <div
          className="rounded-xl p-4 space-y-3"
          style={{ background: "var(--color-card)", border: "1px dashed var(--color-border-strong)" }}
        >
          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-accent)" }}>
            {t("polls.card.startPoll")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs flex-shrink-0" style={{ color: "var(--color-muted-foreground)" }}>
              {t("polls.card.deadlineLabel")}
            </label>
            <select
              className="text-xs rounded px-2 py-1 min-w-0"
              style={{ border: "1px solid var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
              value={deadlineDays}
              onChange={(e) => setDeadlineDays(Number(e.target.value))}
            >
              {[3, 5, 7, 10, 14].map((d) => (
                <option key={d} value={d}>{t("polls.card.daysOption", { count: d })}</option>
              ))}
            </select>
            <Button
              size="sm"
              loading={openPoll.isPending}
              onClick={() => openPoll.mutate({ clubId, deadlineDays })}
            >
              {t("polls.card.openButton")}
            </Button>
          </div>
          {openPoll.error && (
            <p className="text-xs" style={{ color: "var(--color-destructive)" }}>{openPoll.error.message}</p>
          )}
        </div>
      ) : null}

      {recentClosed.length > 0 && (
        <div className="space-y-1.5">
          {recentClosed.map((p) => (
            <Link
              key={p.id}
              to={`/clubs/${clubId}/polls/${p.id}`}
              className="flex items-center justify-between rounded-lg px-3 py-2 text-xs transition-all"
              style={{ background: "var(--color-card)", border: "1px solid var(--color-border)", textDecoration: "none", color: "var(--color-muted-foreground)" }}
            >
              <span>
                {p.state === "UNRESOLVED" ? t("polls.card.unresolvedPoll") : t("polls.card.closedPoll")} · {formatDate(p.openedAt)}
              </span>
              <span style={{ color: p.state === "UNRESOLVED" ? "var(--color-destructive)" : "var(--color-muted-foreground)" }}>
                {p.state === "UNRESOLVED" ? `⚠ ${t("polls.card.needsAdmin")}` : `${p.voteCount} votes`}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
